<template>

  <div class="message-composer border-top bg-white px-3 py-2 flex-shrink-0">

    <div
      v-if="!channels.length"
      class="text-muted text-center py-3">
      {{ $t('No channel available to send messages') }}
      <div>
        <a
          href="https://intercom.help/quickin/pt-BR/articles/16308865-whatsapp-no-quickin"
          target="_blank">
          <i class="fas fa-external-link-alt" /> {{ $t('Learn how to connect the WhatsApp API') }}
        </a>
      </div>
    </div>

    <template v-else>

      <!-- toolbar: outgoing channel -->
      <div class="d-flex align-items-center mb-2">
        <ChannelSelect
          :channels="channels"
          :value="connectionId"
          @input="connectionId = $event" />
      </div>

      <!-- the agent is answering this conversation: the user only writes after
      taking it over -->
      <div
        v-if="isAgentEnabled"
        class="alert alert-info mb-2 p-3">
        <div class="mb-2">
          <i class="fa-solid fa-robot mr-1"></i>
          <span>{{ $t('The agent is answering this conversation.') }}</span>
        </div>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="savingAgent"
          @click="$emit('take-control')">
          <i
            v-if="savingAgent"
            class="fa-solid fa-circle-notch fa-spin mr-1"></i>
          {{ $t('Take control') }}
        </button>
      </div>

      <!-- composer: textarea + send. Enter sends, Shift+Enter adds a new line -->
      <!-- WhatsApp 24h service window is closed: free text is blocked, only templates -->
      <div
        v-else-if="!sessionOpen"
        class="alert alert-warning mb-2 p-3">
        <div class="mb-2">
          <i class="fa-regular fa-clock mr-1"></i>
          <span class="flex-grow-1 mr-2">{{ $t('The 24h window is closed. You can only send a template.') }}</span>
        </div>
        <button
          v-if="isWhatsapp"
          type="button"
          class="btn btn-primary"
          :disabled="sending"
          @click="openTemplatePicker">{{ $t('Send a template') }}</button>
      </div>
      <form
        v-else
        @submit.prevent="send">

        <!-- everything waiting to go out with the next send -->
        <AttachmentList
          v-if="pendingAttachments.length"
          :attachments="pendingAttachments"
          :disabled="busy"
          @remove="removeAttachment" />

        <!-- the recorder sits above the input: typing is never blocked -->
        <AudioRecorder
          v-if="recorderOpen"
          class="mb-2"
          @record="onAudioRecorded"
          @close="recorderOpen = false" />

        <div class="d-flex align-items-end">
          <textarea-autosize
            ref="input"
            v-model="draft"
            class="form-control message-composer-input mr-2"
            :placeholder="$t('Type a message')"
            :min-height="40"
            :max-height="140"
            @keydown.native.enter.exact.prevent="send"
            @keydown.native.enter.shift.exact="() => {}" />

          <button
            type="submit"
            class="btn btn-primary flex-shrink-0"
            :disabled="!canSend">
            <i
              v-if="busy"
              class="fa-solid fa-circle-notch fa-spin"></i>
            <i
              v-else
              class="fa-solid fa-paper-plane"></i>
          </button>
        </div>

        <!-- what this channel can send right now: attachments, recorder, template -->
        <ComposerActions
          class="mt-2"
          :features="features"
          :disabled="busy"
          @attach="addAttachments"
          @record="openRecorder"
          @template="openTemplatePicker" />

        <div
          v-if="errorMessage"
          class="small text-danger mt-1">{{ errorMessage }}</div>

      </form>

    </template>

    <!-- template picker -->
    <WhatsappTemplatePicker
      v-if="showTemplatePicker"
      :connection-id="connectionId"
      @select="onTemplateSelected"
      @close="showTemplatePicker = false" />

    <!-- template preview / fill variables -->
    <WhatsappTemplatePreview
      v-if="previewTemplate"
      :template="previewTemplate"
      :sending="sending"
      @confirm="sendTemplate"
      @close="previewTemplate = null" />

  </div>

</template>

<script>
import AttachmentList from './AttachmentList'
import AudioRecorder from './AudioRecorder'
import ChannelSelect from './ChannelSelect'
import ComposerActions from './ComposerActions'
import WhatsappTemplatePicker from './WhatsappTemplatePicker'
import WhatsappTemplatePreview from './WhatsappTemplatePreview'

// same limit as the media upload middleware (multer)
const MAX_FILE_SIZE = 5 * 1024 * 1024

// only used to key the pending list
let sequence = 0

/*
messageType
the type the api expects for a file, taken from its mime type.
anything that is not a media the channel knows about goes as a document
*/
const messageType = (file) => {
  const mime = file.type || ''

  if (mime.indexOf('image/') === 0) {
    return 'image'
  }

  if (mime.indexOf('video/') === 0) {
    return 'video'
  }

  if (mime.indexOf('audio/') === 0) {
    return 'audio'
  }

  return 'document'
}

/*
createAttachment
wraps a File into the item kept until the message is sent.
nothing is uploaded here: the media is only created when the message goes out
*/
const createAttachment = (file, extra) => {
  sequence = sequence + 1

  return Object.assign({
    id: 'attachment-' + sequence,
    file: file,
    name: file.name,
    size: file.size,
    type: messageType(file)
  }, extra || {})
}

/*
MessageComposer
holds what the user is about to send (text, files and a recording) and turns
it into one message per item: the text, each file typed by its mime and the
recording. the files are only uploaded when the message is sent
*/
export default {
  name: 'MessageComposer',
  components: {
    AttachmentList,
    AudioRecorder,
    ChannelSelect,
    ComposerActions,
    WhatsappTemplatePicker,
    WhatsappTemplatePreview
  },
  props: {
    channels: {
      type: Array,
      default: () => []
    },
    sending: {
      type: Boolean,
      default: false
    },
    // whether the WhatsApp 24h service window is open (free text allowed)
    sessionOpen: {
      type: Boolean,
      default: true
    },
    /*
    isAgentEnabled
    the conversational agent is answering this conversation: nothing is written
    by hand until the user takes it over (take-control)
    */
    isAgentEnabled: {
      type: Boolean,
      default: false
    },
    // the take over is being persisted by the parent
    savingAgent: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      draft: '',
      connectionId: null,
      // files picked with the attach action
      attachments: [],
      // the recording, kept apart because there is at most one and it has a preview
      attachmentAudio: null,
      recorderOpen: false,
      uploading: false,
      errorMessage: null,
      showTemplatePicker: false,
      previewTemplate: null
    }
  },
  computed: {
    selectedChannel () {
      return this.channels.find((channel) => { return channel._id === this.connectionId }) || null
    },
    isWhatsapp () {
      return this.selectedChannel && this.selectedChannel.integration === 'whatsapp'
    },
    /*
    features
    the actions offered next to the input. only reached with the session open:
    the closed window has its own template button
    */
    features () {
      const features = ['attach', 'record']

      if (this.isWhatsapp) {
        features.push('template')
      }

      return features
    },
    // everything that goes out with the next send, the recording last
    pendingAttachments () {
      return this.attachmentAudio ? this.attachments.concat([this.attachmentAudio]) : this.attachments
    },
    // a message is going out or a file is being uploaded
    busy () {
      return this.sending || this.uploading
    },
    hasContent () {
      return !!this.draft.trim() || this.pendingAttachments.length > 0
    },
    canSend () {
      return !this.busy && !this.isAgentEnabled && this.sessionOpen && !!this.connectionId && this.hasContent
    }
  },
  watch: {
    channels: {
      immediate: true,
      handler (channels) {
        // default to the first channel, or keep the current one if still valid
        if (!this.connectionId || !channels.some((channel) => { return channel._id === this.connectionId })) {
          this.connectionId = channels.length ? channels[0]._id : null
        }
      }
    },
    // the outgoing channel is what tells the parent which service window applies
    connectionId: {
      immediate: true,
      handler (connectionId) {
        this.$emit('channel', connectionId)
      }
    }
  },
  beforeDestroy () {
    this.clearAudio()
  },
  methods: {
    /*
    send
    one action sends several messages: the text first, then one message per
    attachment. the text does not depend on any upload, so it goes out right away
    */
    send () {
      if (!this.canSend) {
        return
      }

      const text = this.draft.trim()
      const attachments = this.pendingAttachments

      this.errorMessage = null

      if (text) {
        this.emitMessage({ type: 'text', content: { text: text } })
        this.draft = ''
      }

      if (!attachments.length) {
        return
      }

      this.uploading = true

      Promise
      .all(attachments.map((attachment) => { return this.upload(attachment) }))
      .then((results) => {
        this.sendUploaded(results)
      })
      .finally(() => {
        this.uploading = false
      })
    },
    /*
    upload
    creates the account media for one attachment. it never rejects: the result
    carries either the media or the reason it could not be uploaded
    */
    upload (attachment) {
      const formData = new FormData()
      formData.append('file', attachment.file)

      return this.$store
      .dispatch('createMediaMultipart', formData)
      .then((media) => {
        return { attachment: attachment, media: media, error: null }
      })
      .catch((err) => {
        const status = err.response && err.response.status
        const error = status === 413
          ? this.$t('The file must be at most 5MB.')
          : this.$t('Could not upload the file')

        return { attachment: attachment, media: null, error: error }
      })
    },
    /*
    sendUploaded
    sends one message per uploaded file and keeps the ones that failed, so the
    user can try again without picking them a second time
    */
    sendUploaded (results) {
      const failed = results.filter((result) => { return !result.media })

      results.forEach((result) => {
        if (!result.media) {
          return
        }

        this.emitMessage({
          type: result.attachment.type,
          content: null,
          attachment_media_ids: [result.media._id],
          // not sent to the api: used by the optimistic bubble while it goes out
          media: result.media
        })
      })

      this.errorMessage = failed.length ? failed[0].error : null
      this.keepAttachments(failed.map((result) => { return result.attachment.id }))
    },
    /*
    emitMessage
    { connection_id, type, content, attachment_media_ids }: one message for the parent
    */
    emitMessage (message) {
      this.$emit('send', Object.assign({ connection_id: this.connectionId }, message))
    },
    /*
    addAttachments
    files picked with the attach action, oversized ones are refused up front
    */
    addAttachments (files) {
      const accepted = files.filter((file) => { return file.size <= MAX_FILE_SIZE })

      this.errorMessage = accepted.length === files.length
        ? null
        : this.$t('The file must be at most 5MB.')

      this.attachments = this.attachments.concat(accepted.map((file) => { return createAttachment(file) }))
    },
    /*
    removeAttachment
    */
    removeAttachment (id) {
      if (this.attachmentAudio && this.attachmentAudio.id === id) {
        this.clearAudio()
        return
      }

      this.attachments = this.attachments.filter((attachment) => { return attachment.id !== id })
    },
    /*
    keepAttachments
    keeps only the given attachments, the other ones have already been sent
    */
    keepAttachments (ids) {
      this.attachments = this.attachments.filter((attachment) => { return ids.indexOf(attachment.id) !== -1 })

      if (this.attachmentAudio && ids.indexOf(this.attachmentAudio.id) === -1) {
        this.clearAudio()
      }
    },
    /*
    openRecorder
    */
    openRecorder () {
      if (!this.connectionId) {
        return
      }

      this.errorMessage = null
      this.recorderOpen = true
    },
    /*
    onAudioRecorded
    the recorder is done: the recording becomes an attachment and is only
    uploaded on send. an oversized one is refused, like any other file
    */
    onAudioRecorded (file) {
      this.recorderOpen = false

      if (file.size > MAX_FILE_SIZE) {
        this.errorMessage = this.$t('The file must be at most 5MB.')
        return
      }

      this.clearAudio()

      this.attachmentAudio = createAttachment(file, {
        type: 'audio',
        url: window.URL.createObjectURL(file)
      })
    },
    /*
    clearAudio
    */
    clearAudio () {
      if (!this.attachmentAudio) {
        return
      }

      window.URL.revokeObjectURL(this.attachmentAudio.url)
      this.attachmentAudio = null
    },
    /*
    openTemplatePicker
    */
    openTemplatePicker () {
      if (!this.connectionId) {
        return
      }
      this.showTemplatePicker = true
    },
    /*
    onTemplateSelected
    */
    onTemplateSelected (template) {
      this.showTemplatePicker = false
      this.previewTemplate = template
    },
    /*
    sendTemplate
    { type: 'template', content: { name, language, body, components } }
    the resolved payload comes from the preview (variables already filled)
    */
    sendTemplate (payload) {
      const content = {
        name: payload.name,
        language: payload.language,
        body: payload.body || ''
      }

      if (payload.components && payload.components.length) {
        content.components = payload.components
      }

      this.emitMessage({ type: 'template', content: content })
      this.previewTemplate = null
    }
  }
}
</script>

<style scoped>
/* keep the textarea from being manually resized (it auto-grows instead) */
.message-composer-input {
  resize: none;
}
</style>



// WEBPACK FOOTER //
// src/components/Messaging/composer/MessageComposer.vue